import React, { useRef, useState, useCallback } from 'react';
import { API_BASE_URL } from '../config';

// Inline photos are stored as email attachments and referenced by a relative API path.
// An <img> can't send an auth header, so expand them to absolute URLs carrying the
// token (the API also accepts ?token= for exactly this reason).
const withInlineImages = (html) => {
  const token = localStorage.getItem('authToken') || '';
  return (html || '').replace(
    /(src=["'])(\/api\/emails\/\d+\/attachments\/\d+)(["'])/gi,
    (_m, a, path, z) => `${a}${API_BASE_URL || ''}${path}?token=${encodeURIComponent(token)}${z}`
  );
};

// Renders an email body. When the original HTML is available (imported mail), it renders
// the real formatted email inside a SANDBOXED iframe — faithful layout/logos/buttons, but
// with scripts disabled (safe from malicious mail) and the email's CSS isolated from the
// app. Falls back to plain text (dimming the signature after the "--" separator) for our
// own sent messages and plain-text mail.
export default function EmailBody({ text, html, style }) {
  const iframeRef = useRef(null);
  const [height, setHeight] = useState(140);

  const onLoad = useCallback(() => {
    try {
      const doc = iframeRef.current && iframeRef.current.contentDocument;
      if (doc) {
        const h = Math.max(
          doc.documentElement ? doc.documentElement.scrollHeight : 0,
          doc.body ? doc.body.scrollHeight : 0
        );
        if (h) setHeight(Math.min(h + 12, 4000));
      }
    } catch (e) { /* leave default height */ }
  }, []);

  if (html && html.trim()) {
    const srcDoc =
      '<!doctype html><html><head><base target="_blank">' +
      '<meta name="viewport" content="width=device-width, initial-scale=1">' +
      '<style>html,body{margin:0;padding:10px;font-family:-apple-system,Segoe UI,Roboto,Arial,sans-serif;color:#0f172a;}' +
      'img{max-width:100%;height:auto;}table{max-width:100%;}</style>' +
      '</head><body>' + withInlineImages(html) + '</body></html>';
    return (
      <div style={style}>
        <iframe
          ref={iframeRef}
          title="Email message"
          onLoad={onLoad}
          // No allow-scripts → email JS never runs. allow-same-origin lets us measure the
          // height; allow-popups lets links open in a new tab.
          sandbox="allow-same-origin allow-popups allow-popups-to-escape-sandbox"
          srcDoc={srcDoc}
          style={{ width: '100%', border: 'none', height, background: 'white', display: 'block' }}
        />
      </div>
    );
  }

  const raw = text || '';
  const idx = raw.search(/\n--\n/);
  const body = idx >= 0 ? raw.slice(0, idx) : raw;
  const sig = idx >= 0 ? raw.slice(idx + 4) : '';

  return (
    <div style={style}>
      <div style={{ whiteSpace: 'pre-wrap' }}>{body}</div>
      {sig && (
        <div style={{
          whiteSpace: 'pre-wrap', marginTop: 10, paddingTop: 8,
          borderTop: '1px solid #e2e8f0', color: '#94a3b8', fontSize: 12,
        }}>
          {sig}
        </div>
      )}
    </div>
  );
}
